/**
 * THE READ-PATH CONTRACT — one shape for "has this domain been fetched yet, and did it
 * work", shared by every store that reads through an API module.
 *
 * ============================================================================
 * WHY THIS FILE EXISTS. src/api/errors.ts fixed one half of Batch 7 §21 — "Do not allow
 * every component to invent a different API error model" — by giving the app one thrown
 * value and one `kind` to branch on. This is the other half, and the gap was wider: before
 * it, the app had FIVE different ways of saying a request was in flight and no way at all
 * of saying a read had failed.
 *
 *   authStore      `pending: boolean`
 *   songsStore     `generating: boolean`
 *   dnaStore       `analyzing` + `regenerating` + `error: string`
 *   mixerStore     `generating` + `error: string`
 *   studioStore    `pendingAction: string | null` + `pendingCover: boolean`
 *   supportStore   a `TicketSubmission` union returned from the action
 *
 * Every one of those describes a WRITE — a generation, a submission, an analysis. Not one
 * of them describes a READ, because until now nothing in the app performed one: the six
 * `getX()` functions in src/api/ had no callers outside their own modules, so there was no
 * "loading the library" state to hold and no failed fetch to report. A store cannot have
 * a loading state for a request it never makes.
 * ============================================================================
 *
 * WHAT THIS IS NOT. Not a replacement for any of the six above. They are per-operation
 * write states with their own meanings — `analyzing` leaves the analysis on screen while
 * `generating` hides it — and collapsing them into one status enum would lose exactly the
 * distinctions their comments were written to preserve. §26 says remove duplication, not
 * remove difference. This adds the one state none of them holds.
 *
 * FOUR STATES, AND `idle` IS THE ONE THAT IS USUALLY MISSING. A `loading: boolean` cannot
 * tell "we have not asked" from "we asked and it is done", so an empty list means either
 * "you have no playlists" or "the fetch has not started" and the screen has to guess. That
 * is the same three-state problem `authStore.sessionChecked` documents for the session, and
 * it produces the same defect: an empty state rendered over data that is about to arrive.
 *
 * THE ERROR IS AN `ApiErrorShape`, NOT AN `ApiError`. Zustand state is compared, shown in
 * devtools and snapshotted between tests; an `Error` carries a stack that differs on every
 * throw, so holding the instance makes equality checks meaningless. `toApiErrorShape`
 * exists for precisely this — see api/errors.ts, which says so at the type.
 */
import { toApiErrorShape, type ApiErrorShape } from '../api/errors';

/**
 * Where a domain's read has got to.
 *
 *   idle     never requested. NOT the same as "requested and empty".
 *   loading  in flight. A first load has nothing to show; a refresh has stale data.
 *   ready    the server answered. An empty result here is a real empty state.
 *   error    the request failed. `error` says how, and the local data is untouched.
 */
export type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * One domain's read state.
 *
 * `error` is non-null only when `status === 'error'`; a successful load clears it, so a
 * screen cannot render a stale failure beside fresh data.
 */
export interface LoadState {
  status: LoadStatus;
  error: ApiErrorShape | null;
}

/**
 * The starting value for every store: nothing has been asked.
 *
 * A shared frozen constant rather than a factory, because it holds no per-instance data
 * and every store's initial state can point at the same object. Frozen so a store that
 * mutated it in place could not change the initial state of all the others — which is the
 * kind of defect that only shows up as one test file failing after another one runs.
 */
export const IDLE_LOAD: LoadState = Object.freeze({ status: 'idle', error: null });

/** In flight. Clears any previous failure, so a retry does not show the old message. */
export const LOADING_LOAD: LoadState = Object.freeze({ status: 'loading', error: null });

/** The server answered. */
export const READY_LOAD: LoadState = Object.freeze({ status: 'ready', error: null });

/**
 * The request failed, normalised through the one error contract.
 *
 * Takes the raw caught value rather than an `ApiError`, because that is what a `catch`
 * hands over: `toApiErrorShape` already knows how to turn a mock's `ApiError`, a failed
 * `fetch`'s `TypeError` and this app's own capability-refusal classes into one shape. A
 * store calling this cannot get that mapping wrong, and there is only one copy of it.
 */
export function failedLoad(err: unknown): LoadState {
  return { status: 'error', error: toApiErrorShape(err) };
}

/**
 * True while a FIRST load is in flight — nothing has ever arrived, so there is nothing to
 * show behind the spinner.
 *
 * The predicate a screen wants for "show a skeleton instead of the list". Distinguished
 * from a refresh deliberately: re-reading a library that is already on screen should not
 * blank it, and `status === 'loading'` alone cannot tell the two apart. The caller passes
 * whether it currently holds anything.
 */
export function isFirstLoad(load: LoadState, hasData: boolean): boolean {
  return load.status === 'loading' && !hasData;
}

/**
 * True when an empty collection means "there is genuinely nothing", rather than "we have
 * not looked yet".
 *
 * The distinction the `idle` state exists for. A screen that renders its designed empty
 * state on `items.length === 0` alone shows "You have no playlists" during the fetch that
 * is about to return three, which is the defect this whole file is here to make
 * unrepresentable.
 */
export function isConfirmedEmpty(load: LoadState, hasData: boolean): boolean {
  return load.status === 'ready' && !hasData;
}
